/**
 * Client for the NestJS backend (`apps/api`), used by the vocabulary read slice.
 *
 * This exists so the SPA can run BOTH read paths against the SAME database and
 * be diffed: the PostgREST path stays the fallback, exactly as
 * `lib/public-read.ts` treats its cached endpoint. That is deliberate — a
 * half-finished backend migration must never be able to empty a page.
 *
 * Two rules carried over from `public-read.ts`, because they were right there:
 *
 *  - a transport/deployment failure (`ApiUnavailableError`) means "use the other
 *    path"; anything else is rethrown untouched, so a real bug is not swallowed;
 *  - the timeout is short. A cached/edge read should beat the query it replaces;
 *    if it does not, the fallback should win quickly rather than hang the page.
 *
 * Enabled only when BOTH the flag is on and a base URL is configured — a flag
 * with no URL would otherwise turn every read into a failed request and a
 * fallback, which is pure latency for no benefit.
 */

import { auth } from "./auth-client";

/** A backend read should be faster than the query it replaces. */
const TIMEOUT_MS = 5_000;

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");


/**
 * Access token, cached in memory.
 *
 * Every API call used to `await auth.getSession()`, which touches
 * storage on each request and can kick a refresh. React Query fires several
 * queries on mount, so that was several identical awaits per page. This caches
 * the token and keeps it fresh from the auth state stream instead.
 */
let cachedAccessToken: string | null = null;

auth.onAuthStateChange((_event, session) => {
  cachedAccessToken = session?.access_token ?? null;
});

async function accessToken(): Promise<string | null> {
  if (cachedAccessToken) return cachedAccessToken;
  const {
    data: { session },
  } = await auth.getSession();
  cachedAccessToken = session?.access_token ?? null;
  return cachedAccessToken;
}

/**
 * Recover from an access token the API refused.
 *
 * `cachedAccessToken` is refreshed from the auth state stream and nothing rotates
 * the token in the background, while an access token lives one hour. A tab left
 * open across that boundary keeps sending a dead token, so every call 401s and
 * the page renders empty while the header still shows the signed-in learner.
 * Retrying once with a rotated token is what turns that into a slow first request
 * rather than missing data.
 *
 * The token that failed is passed through so a second, concurrent 401 rides the
 * rotation already in flight instead of rotating again. `issuedForUserId` is the
 * account the call was made for: if the session changed since, the replay would
 * run as someone else and is refused.
 */
async function retryAfterUnauthorized(
  failedToken: string | null,
  issuedForUserId: string | null,
): Promise<string | null> {
  const fresh = await auth.refreshAccessToken(failedToken, issuedForUserId);
  cachedAccessToken = fresh;
  return fresh;
}

/**
 * In-flight GET de-duplication.
 *
 * Several components can request the same endpoint in the same tick (the deck
 * and its stats on the vocabulary page do exactly that). React Query de-dupes by
 * query key, but not across two hooks with different keys hitting one endpoint.
 *
 * Only GETs are de-duped, and only while in flight: nothing is cached beyond the
 * response, so this cannot serve stale data.
 */
const inFlight = new Map<string, Promise<unknown>>();

/** The backend is not reachable/deployed — the caller should use its fallback. */
export class ApiUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiUnavailableError";
  }
}

interface ApiGetOptions {
  /** Abort the request (e.g. React Query cancellation). */
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * GET a backend endpoint as the signed-in user.
 *
 * Sends the Supabase access token, so the backend verifies the same JWT the SPA
 * already holds and RLS evaluates the same policies. A signed-out caller sends
 * no token: the backend treats that as anon rather than an error, which is what
 * makes shared cacheable reads possible later.
 */
export async function apiGet<T>(path: string, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("GET", path, undefined, options);
}

/** POST a JSON body. Same fallback semantics as `apiGet`. */
export async function apiPost<T>(path: string, body: unknown, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("POST", path, body, options);
}

/** PUT a JSON body. Used where the request replaces a whole resource (e.g. ordering). */
export async function apiPut<T>(path: string, body: unknown, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("PUT", path, body, options);
}

/** PATCH a JSON body. */
export async function apiPatch<T>(path: string, body: unknown, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("PATCH", path, body, options);
}

/** DELETE. `path` carries the identifier, e.g. `/v1/vocabulary/${encodeURIComponent(word)}`. */
export async function apiDelete<T>(path: string, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("DELETE", path, undefined, options);
}

/**
 * POST JSON and parse the reply — the replacement for `functions.invoke()`.
 *
 * Same error contract as the other helpers (the backend's `message` is surfaced
 * verbatim, with `status` and `code` attached), so a call site that used to
 * inspect `{ data, error }` becomes one `await` in a `try`. Streaming endpoints
 * use `apiPostStream` instead; this one buffers.
 */
export async function apiPostJson<T>(path: string, body: unknown, options: ApiGetOptions = {}): Promise<T> {
  return apiRequest<T>("POST", path, body, options);
}

/**
 * POST and hand back the **raw response**, for endpoints that stream.
 *
 * `apiRequest` parses JSON, which would buffer a generation endpoint until the
 * last token — the opposite of what streaming is for. Nothing here is parsed and
 * nothing is cached, so the caller decides how to read the body (`text/plain`
 * partial JSON for vocabulary enrichment, `text/event-stream` for chat).
 *
 * No timeout by default: a read budget is meant to keep a page responsive, but a
 * generation legitimately runs longer, and aborting mid-body turns a partial
 * result into a network error. A non-2xx response is returned, not thrown — the
 * caller already has to handle refusal (the old `fetch` call sites all checked
 * `response.ok`) and a thrown error would lose the body it wants to read.
 */
export async function apiPostStream(path: string, body: unknown, options: ApiGetOptions = {}): Promise<Response> {
  if (!baseUrl) throw new ApiUnavailableError("VITE_API_BASE_URL is not set");

  const send = (token: string | null) =>
    fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

  const token = await accessToken();
  // Captured before the request: the refresh guard compares the account that
  // issued it with the one owning the session when the 401 comes back.
  const issuedForUserId = auth.getUserId();
  const response = await send(token);
  if (response.status !== 401) return response;

  // Nothing was consumed yet, so replaying a refused request with a rotated
  // token cannot duplicate work — a 401 never reached the handler.
  const fresh = await retryAfterUnauthorized(token, issuedForUserId);
  if (!fresh || fresh === token) return response;
  return send(fresh);
}

async function apiRequest<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body: unknown,
  options: ApiGetOptions,
): Promise<T> {
  // A caller with its own AbortSignal (React Query always passes one) must not be
  // handed another caller's promise, so only un-aborted GETs are coalesced.
  if (method === "GET" && !options.signal) {
    const existing = inFlight.get(path);
    if (existing) return existing as Promise<T>;
  }

  const promise = performRequest<T>(method, path, body, options);

  if (method === "GET" && !options.signal) {
    inFlight.set(path, promise);
    // `finally` on a derived promise so a rejection still clears the entry.
    promise.finally(() => inFlight.delete(path)).catch(() => undefined);
  }

  return promise;
}

async function performRequest<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body: unknown,
  options: ApiGetOptions,
): Promise<T> {
  if (!baseUrl) throw new ApiUnavailableError("VITE_API_BASE_URL is not set");

  const send = async (token: string | null): Promise<Response> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? TIMEOUT_MS);

    // Compose the caller's abort signal with our timeout.
    const onAbort = () => controller.abort();
    options.signal?.addEventListener("abort", onAbort);

    try {
      return await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal,
      });
    } catch (error) {
      // Aborts and network failures are transport problems, not logic problems.
      if (error instanceof ApiUnavailableError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiUnavailableError(`${path} timed out`);
      }
      if (error instanceof TypeError) {
        // fetch rejects with TypeError on DNS/connection failure or CORS.
        throw new ApiUnavailableError(`${path} unreachable: ${error.message}`);
      }
      throw error;
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener("abort", onAbort);
    }
  };

  const token = await accessToken();
  // See `retryAfterUnauthorized`: the account that issued the call is part of the
  // retry decision, and it must be read before the request, not after the 401.
  const issuedForUserId = auth.getUserId();
  let response = await send(token);

  // See `retryAfterUnauthorized`: a tab open past the access token's hour sends
  // a dead token and every call 401s, which reads as "my data is gone".
  if (response.status === 401) {
    const fresh = await retryAfterUnauthorized(token, issuedForUserId);
    if (fresh && fresh !== token) response = await send(fresh);
  }

  if (response.status === 404) {
    // The endpoint is not deployed (local dev without apps/api). Not an error
    // the user should ever see; it means "fall back".
    throw new ApiUnavailableError(`${path} is not available`);
  }

  if (!response.ok) {
    // A 4xx from a database function carries the message the user needs
    // ("admin privileges required", "column … does not exist"). Surface it
    // verbatim rather than flattening to a status code.
    const payload = await response.json().catch(() => null);
    const message = payload?.message ?? `${response.status} ${response.statusText}`;
    const error = new Error(message);
    (error as Error & { status?: number; code?: string }).status = response.status;
    (error as Error & { status?: number; code?: string }).code = payload?.code;
    throw error;
  }

  return (await response.json()) as T;
}
